import { X } from "lucide-react";
import { useListContext, useTranslate } from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import type { Company } from "../types";
import {
  type AccountsSavedView,
  useAccountsSavedViews,
} from "./useAccountsSavedViews";

/**
 * The named filter sets of the accounts list: one click applies a view, the box
 * saves whatever the filter bar currently holds under a name.
 *
 * It sits in the filter row rather than in a dialog because that is where the
 * state it captures is: a view is only ever saved right after the filters that
 * produced it were set.
 */
export const AccountsSavedViews = () => {
  const translate = useTranslate();
  const { filterValues, setFilters, sort, setSort } = useListContext<Company>();
  const { views, saveView, deleteView } = useAccountsSavedViews();
  const [name, setName] = useState("");
  const [isDuplicate, setIsDuplicate] = useState(false);

  const applyView = (view: AccountsSavedView) => {
    // Copies, both ways: the list context and the stored view must never end up
    // sharing the object one of them is free to change.
    setFilters({ ...view.filter });
    // A view saved before the sort was part of them keeps the current order
    // rather than snapping the list back to the default.
    if (view.sort) setSort({ ...view.sort });
  };

  const handleSave = () => {
    const trimmedName = name.trim();
    if (trimmedName === "") return;

    if (views.some((view) => view.name === trimmedName)) {
      setIsDuplicate(true);
      return;
    }

    // A copy, not the live filter object: the saved view must keep the values
    // it was saved with once the filter bar moves on.
    saveView({
      name: trimmedName,
      filter: { ...filterValues },
      sort: { ...sort },
    });
    setName("");
  };

  const handleNameChange = (value: string) => {
    setName(value);
    setIsDuplicate(false);
  };

  return (
    <div className="flex flex-row flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-muted-foreground">
        {translate("crm.dashboard.accounts.saved_views.title")}
      </span>

      {views.length === 0 ? (
        <span className="text-xs text-muted-foreground">
          {translate("crm.dashboard.accounts.saved_views.empty")}
        </span>
      ) : (
        views.map((view) => (
          <span
            key={view.name}
            className="inline-flex items-center rounded-md border"
          >
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 rounded-r-none px-2 cursor-pointer"
              onClick={() => applyView(view)}
            >
              {view.name}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7 rounded-l-none cursor-pointer"
              /*
                The label carries the view's name: several delete buttons sit
                side by side, and "Delete view" alone names none of them.
              */
              aria-label={`${translate(
                "crm.dashboard.accounts.saved_views.delete",
              )}: ${view.name}`}
              onClick={() => deleteView(view.name)}
            >
              <X className="size-3" />
            </Button>
          </span>
        ))
      )}

      <form
        className="flex items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          handleSave();
        }}
      >
        <Input
          value={name}
          onChange={(event) => handleNameChange(event.target.value)}
          aria-label={translate("crm.dashboard.accounts.saved_views.name")}
          placeholder={translate("crm.dashboard.accounts.saved_views.name")}
          className="h-7 w-40"
        />
        <Button
          type="submit"
          variant="outline"
          size="sm"
          className="h-7 cursor-pointer"
          disabled={name.trim() === ""}
        >
          {translate("crm.dashboard.accounts.saved_views.save")}
        </Button>
      </form>

      {isDuplicate && (
        <p role="alert" className="text-xs text-destructive">
          {translate("crm.dashboard.accounts.saved_views.duplicate")}
        </p>
      )}
    </div>
  );
};
